import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { PhonogramViewStore } from "../../store/PhonogramViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReferenceSet } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowBoolean } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";

export type PhonogramViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  trackNameValue?: { displayId: string; id: string } | null;
  trackName: string;
  performersValues: { displayId: string; id: string }[];
  albumNameValue?: { displayId: string; id: string } | null;
  albumName: string;
  publicationYearValue?: { displayId: string; id: string } | null;
  publicationYear: number | undefined;
  isrcValue?: { displayId: string; id: string } | null;
  isrc: string;
  originalProducerValue?: { displayId: string; id: string } | null;
  originalProducer: string;
  audioFileFiles: CrudRows;
  audioFileViewAction?: IMenuAction[];
  mainAudioValue?: { displayId: string; id: string } | null;
  mainAudio: boolean;
  linksRows: CrudRows;
  linksVisibleColumns: GridColumn[];
  linksSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  notesValue?: { displayId: string; id: string } | null;
  notes: string;
  phonogramViewStore: PhonogramViewStore;
};

export const PhonogramViewForm: React.FC<PhonogramViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    trackName = "",
    performersValues,
    albumName = "",
    publicationYear = 0,
    isrc = "",
    originalProducer = "",
    audioFileFiles,
    audioFileViewAction,
    mainAudio = false,
    linksRows,
    linksVisibleColumns,
    linksSetColumnWiths,
    notes = "",
    phonogramViewStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="phonogramInfo"
            title={langStore.groupPhonogramPhonogramInfoLabel}
            collapsible={false}
            expanded={phonogramViewStore.getCardState("phonogramInfo")}
            toggleExpand={() =>
              phonogramViewStore.toggleCardState("phonogramInfo")
            }
          >
            <FormControl fullWidth>
              <ShowMultiLine
                value={trackName}
                label={langStore.PhonogramTrackNameLabel}
              />
              <ShowReferenceSet
                label={langStore.PhonogramPerformersLabel}
                selectedValues={performersValues}
              />
              <ShowMultiLine
                value={albumName}
                label={langStore.PhonogramAlbumNameLabel}
              />
              <ShowNumber
                value={publicationYear}
                label={langStore.PhonogramPublicationYearLabel}
              />
              <ShowMultiLine
                value={isrc}
                label={langStore.PhonogramIsrcLabel}
              />
              <ShowMultiLine
                value={originalProducer}
                label={langStore.PhonogramOriginalProducerLabel}
              />
              <ShowFilesCompact
                rows={audioFileFiles}
                showLabel={true}
                label={langStore.PhonogramAudioFileLabel}
                menuActions={audioFileViewAction}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
              />
              <ShowBoolean
                value={mainAudio}
                label={langStore.PhonogramMainAudioLabel}
              />
              <ShowItemsCompact
                rows={linksRows}
                showLabel={true}
                label={langStore.PhonogramLinksLabel}
                columns={linksVisibleColumns}
                setColumnWidths={linksSetColumnWiths}
                entityName="MusicalWorkLink"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("musicalWorkLink", rowId)
                }
              />
              <ShowMultiLine
                value={notes}
                label={langStore.PhonogramNotesLabel}
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
